import React, { useState, useEffect } from 'react';
import { stripeService } from '../services/stripe';
import { getCurrentUserId } from '../services/database';
import { Check, CreditCard, AlertCircle } from 'lucide-react';

const EnterprisePayment = ({ onCancel, isRenewal = false, organizationName = '', enterpriseId = '' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [formData, setFormData] = useState({
    name: organizationName || '', // Prefill with organization name if provided
    seatLimit: 10,
    expirationDays: 360
  });
  
  // Update form data when organizationName prop changes
  useEffect(() => {
    if (organizationName) {
      setFormData(prev => ({
        ...prev,
        name: organizationName
      }));
    }
  }, [organizationName]);
  
  // Add a derived state for form validation
  const isValid = formData.name.trim().length > 0;
  
  const seatOptions = [
    { value: 5, label: '5 Seats' },
    { value: 10, label: '10 Seats' },
    { value: 25, label: '25 Seats' },
    { value: 50, label: '50 Seats' },
    { value: 100, label: '100 Seats' }
  ];
  
  // Updated to use flat pricing of $540 per seat with no volume discounts
  const getPricePerSeat = () => {
    return 540; // Flat $540 per seat with no volume discounts
  };
  
  const getTotalPrice = () => {
    return formData.seatLimit * getPricePerSeat();
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'seatLimit' ? parseInt(value) : value
    }));
    
    // Clear name error when user types in the name field
    if (name === 'name' && value.trim() && nameError) {
      setNameError(null);
    }
  };
  
  // For testing Stripe service availability
  useEffect(() => {
    const testStripeService = async () => {
      try {
        console.log("Testing Stripe service...");
        const stripeInstance = await stripeService.getStripe();
        console.log("Stripe instance available:", !!stripeInstance);
      } catch (error) {
        console.error("Error testing Stripe service:", error);
      }
    };
    
    testStripeService();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setNameError(null);
    setError(null);
    
    // Validate organization name with visual feedback
    if (!formData.name.trim()) {
      setNameError("Please enter your organization name");
      return;
    }
    
    if (isSubmitting) return;
  
    setIsSubmitting(true);
    setIsLoading(true);
    
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      
      console.log(`Starting enterprise ${isRenewal ? 'renewal' : 'payment'} process`);
      
      // Prepare payment data
      const enterprisePaymentData = {
        userId,
        price: getTotalPrice(),
        period: formData.expirationDays || 360,
        title: `${isRenewal ? 'Enterprise Renewal' : 'Enterprise Plan'} (${formData.seatLimit} seats)`,
        isEnterprise: true,
        isRenewal,
        enterpriseId: enterpriseId, // Include enterpriseId in the payment data
        enterpriseData: {
          name: formData.name,
          seatLimit: formData.seatLimit
        }
      };
      
      console.log("Enterprise payment data:", enterprisePaymentData);
      
      // Create checkout session
      const result = await stripeService.createCheckoutSession(enterprisePaymentData);
      console.log("Checkout session result:", result);
      
      // The redirect should happen automatically in createCheckoutSession
    } catch (error) {
      console.error(`Error processing enterprise ${isRenewal ? 'renewal' : 'payment'}:`, error);
      setError(error.message || `Failed to process ${isRenewal ? 'renewal' : 'payment'}. Please try again.`);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-semibold mb-4">
        {isRenewal ? 'Renew Enterprise Account' : 'Enterprise Account Setup'}
      </h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Organization Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-black ${
              nameError ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={isRenewal ? "Current organization name" : "Enter your organization name"}
            disabled={isLoading}
          />
          {nameError && (
            <div className="flex items-center mt-2 text-red-600">
              <AlertCircle className="h-4 w-4 mr-2" />
              <span className="text-sm">{nameError}</span>
            </div>
          )}
          {isRenewal && (
            <p className="text-sm text-gray-600 mt-1">
              You can update the organization name if needed.
            </p>
          )}
        </div>
        
        <div>
          <label htmlFor="seatLimit" className="block text-sm font-medium text-gray-700 mb-2">
            Number of Seats
          </label>
          <select
            id="seatLimit"
            name="seatLimit"
            value={formData.seatLimit}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
            disabled={isLoading}
          >
            {seatOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} - ${(option.value * getPricePerSeat()).toLocaleString()}
              </option>
            ))}
          </select>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-800 mb-3">
            {isRenewal ? 'Renewal Benefits' : 'Enterprise Benefits'}
          </h3>
          <ul className="space-y-2">
            {isRenewal && (
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Restore access for all team members immediately</span>
              </li>
            )}
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                {isRenewal ? 'Continue centralized team management' : 'Centralized team management'}
              </span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Simple user access control</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">All users inherit enterprise account status</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                {isRenewal ? 'Continue annual access for your team' : 'Annual access for your entire team'}
              </span>
            </li>
          </ul>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-bold text-gray-900">
                {isRenewal ? 'Renewal Price' : 'Total Price'}
              </h3>
              <p className="text-sm text-gray-600">Annual plan (365 days)</p>
            </div>
            <div className="text-xl font-bold">${getTotalPrice().toLocaleString()}</div>
          </div>
        </div>
        
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            className={`flex-1 px-4 py-2 ${!isValid ? 'bg-gray-400 cursor-not-allowed' : 'bg-black hover:bg-gray-800'} text-white rounded-md focus:outline-none focus:ring-2 focus:ring-black flex items-center justify-center relative group`}
            disabled={isLoading || !isValid}
            onClick={(e) => {
              if (!formData.name.trim()) {
                e.preventDefault();
                setNameError("Please enter your organization name");
              }
            }}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                {isRenewal ? 'Renew Account' : 'Purchase Enterprise Account'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EnterprisePayment;